import React from 'react';

import { isNil } from 'es-toolkit';

import { Nullable } from '@clearverify/types';

import {
  PeoPayrollProviderRevenueShareDocument,
  PeoPayrollProviderUpdate,
  usePeoPayrollProviderRevenueShareQuery,
  usePeoPayrollProviderRevenueShareUpdateMutation,
  useRevenueShareSettingDeleteMutationMutation,
} from 'src/api';

import { useNotification } from '../app';

export const usePeoPayrollProviderRevenueShare = (id: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { data, loading } = usePeoPayrollProviderRevenueShareQuery({
    variables: {
      where: { id },
    },
    skip: !id,
  });

  const peoPayrollProviderBilling = React.useMemo(
    () => data?.peoPayrollProvider,
    [data?.peoPayrollProvider],
  );

  const [peoPayrollProviderRevenueShareUpdateMutation, { loading: updating }] =
    usePeoPayrollProviderRevenueShareUpdateMutation({
      refetchQueries: [PeoPayrollProviderRevenueShareDocument],
      context: createApolloContext({
        errorMessage: "Can't update Peo Payroll Provider revenue share",
        successMessage: 'Peo Payroll Provider revenue share has been successfully updated',
      }),
    });

  const [peoPayrollProviderBillingDeleteMutation, { loading: deleting }] =
    useRevenueShareSettingDeleteMutationMutation({
      refetchQueries: [PeoPayrollProviderRevenueShareDocument],
      context: createApolloContext({
        errorMessage: "Can't update Peo Payroll Provider revenue share",
        successMessage: 'Peo Payroll Provider revenue share has been successfully updated',
      }),
    });

  const updatePeoPayrollProviderRevenueShare = async (
    data: Omit<PeoPayrollProviderUpdate, 'id'>,
  ) => {
    if (isNil(id)) {
      return;
    }

    await peoPayrollProviderRevenueShareUpdateMutation({
      variables: {
        where: { id },
        data,
      },
    });
  };

  const deletePeoPayrollProviderBilling = async (data: { id: Nullable<string> }) => {
    if (isNil(data.id)) {
      return;
    }

    await peoPayrollProviderBillingDeleteMutation({
      variables: {
        filter: {
          id: data.id,
        },
      },
    });
  };

  return {
    peoPayrollProviderBilling,
    updatePeoPayrollProviderRevenueShare,
    deletePeoPayrollProviderBilling,
    loading: loading || updating || deleting,
  };
};
